import React from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, Preloader, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ErrorBoundary } from '@/modules/core/components';

import useTermsAndConditionsDocsQuery, { DocsEntity } from '../docs/useTermsAndConditionsDocs.query';
import { useInvestor } from '../hooks';
import InfoModal from '../StartInvesting/InvestmentPath/InfoModal';

import { useGetInvestorAccountId } from './hook';

const StyledLink = styled(Anchor)`
  color: ${({ theme }) => theme.colors.textBody};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;

const InvestmentsFooter = () => {
  const { t } = useTranslation();
  const accountId = useGetInvestorAccountId();
  const { docs, isLoading: isDocsLoading } = useTermsAndConditionsDocsQuery();

  const {
    data: { externalAccountNumber },
  } = useInvestor(accountId);
  // const [isExpanded, setIsExpanded] = useState(false);
  // const handleToggleExpanded = useCallback(() => {
  //   setIsExpanded(!isExpanded);
  // }, [isExpanded]);

  const getWrapper = (item: DocsEntity) => {
    if (item.isModal) {
      return (
        <>
          <InfoModal key={item.title} title={item.title} text={item.text || ''}>
            <StyledLink data-testid={`investment_footer_${item.title}`}>
              <Text size="medium" weight={400}>
                {item.title}
              </Text>
            </StyledLink>
          </InfoModal>
        </>
      );
    }
    return (
      <>
        <StyledLink href={item.src} target="_blank" data-testid={`investment_footer_${item.title}`}>
          <Text size="medium" weight={400}>
            {item.title}
          </Text>
        </StyledLink>
      </>
    );
  };

  return (
    <Box direction="column" border={{ side: 'top', color: 'border1' }} margin={{ top: 'l' }}>
      <Box direction="row" align="center" data-testid="investment_footer" pad={{ vertical: 'l' }}>
        <Box direction="row" style={{ flex: 4 }}>
          <Box fill direction="column">
            <ErrorBoundary>
              <Box direction="row" wrap style={{ display: 'block' }}>
                {isDocsLoading && <Preloader testId="doc_preloader" size={12} />}
                {docs.map((item) => {
                  const lineBreak = item.lineBreak && (
                    <Box
                      style={{ display: 'block' }}
                      width="10px"
                      margin={{ bottom: 'spacing12' }}
                      background="red"
                    />
                  );

                  return (
                    <>
                      <Box
                        style={{ display: 'inline-block' }}
                        key={item.title}
                        margin={{ right: 'spacing12', bottom: 'spacing2' }}
                      >
                        {getWrapper(item)}
                      </Box>
                      {lineBreak}
                    </>
                  );
                })}
              </Box>
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>

      <Box pad={{ bottom: 'spacing24' }}>
        {externalAccountNumber && (
          <Text
            size="medium"
            weight={400}
            color="textSecondary"
            margin={{ bottom: 'spacing2' }}
            style={{ lineHeight: '20px' }}
          >
            {`${t('DriveWealth account number:')} ${externalAccountNumber}`}
          </Text>
        )}
        <Text
          size="medium"
          weight={400}
          color="textSecondary"
          margin={{ bottom: 'spacing2' }}
          style={{ lineHeight: '20px' }}
        >
          {t('Past performance of investments is simply an indicator and is no gurantee of future results.')}
        </Text>
        <Text
          size="medium"
          weight={400}
          color="textSecondary"
          margin={{ bottom: 'spacing2' }}
          style={{ lineHeight: '20px' }}
        >
          {t(
            'Investment advisory services offered through Intellicents investment solutions, inc., an SEC registered investment adviser.'
          )}
        </Text>

        <Text
          size="medium"
          weight={400}
          color="textSecondary"
          style={{ lineHeight: '20px' }}
          margin={{ top: 'l', bottom: 'spacing2' }}
        >
          <Text weight={700}>{t('30 Day Performance:')}</Text>
          {t(
            ' calculated by subtracting your balance 30 days ago from your current balance, then adjusting for deposits and withdrawals.'
          )}
        </Text>
        <Text
          size="medium"
          weight={400}
          color="textSecondary"
          margin={{ bottom: 'spacing2' }}
          style={{ lineHeight: '20px' }}
        >
          <Text weight={700}>{t('YTD Performance:')}</Text>
          {t(
            ' calculated by subtracting your starting balance this year from your current balance, then adjusting for deposits and withdrawals.'
          )}
        </Text>
        <Text
          size="medium"
          weight={400}
          color="textSecondary"
          margin={{ bottom: 'spacing2' }}
          style={{ lineHeight: '20px' }}
        >
          <Text weight={700}>{t('Lifetime Performance:')}</Text>
          {t(
            ' calculates your total profit/loss since you opened the account, based on all deposits, withdrawals, and current balance.'
          )}
        </Text>
      </Box>
    </Box>
  );
};
export default InvestmentsFooter;
